import { inject, Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { environment } from '../../environments/environment';
import { SupabaseAuthService } from './supabase-auth.service';
import { firstValueFrom, take } from 'rxjs';
import { SupabaseDatabaseService } from './supabase-database.service';
import TYPES from '../constants/types';

@Injectable({ providedIn: 'root' })
export class PushService extends SupabaseDatabaseService<PushSubscription> {

  private supabaseAuthService = inject(SupabaseAuthService);
  swPush: SwPush = inject(SwPush);

  async requestPermissionAndSubscribe(userId: string) {
    if (!this.swPush.isEnabled) {
      console.warn('Service Worker não habilitado');
      return;
    }

    const existSub = await this.getExistingPushSubscription();

    if(existSub){
      const isValidSub = await this.verifyValidSubscription(userId, existSub.endpoint);
      if(!isValidSub){
        await this.cancelExistingPushSubscription();
        this.subscribeToNotifications();
      }
    }else{
      this.subscribeToNotifications();
    }
  }

  async subscribeToNotifications(){
    const sub = await this.swPush.requestSubscription({
      serverPublicKey: environment.VAPID_PUBLIC_KEY
    });

    const session = await this.supabaseAuthService.getSession();
    const accessToken = session?.data?.session?.access_token;
    
    if (!accessToken) throw new Error('Not authenticated');

    await fetch('/functions/v1/save-push-subscription', {
      method: 'POST',
      headers: { 
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json' 
      },
      body: JSON.stringify({
        subscription: sub
      })
    });
  }

  async cancelExistingPushSubscription(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) return false;

    const registration = await navigator.serviceWorker.ready;
    const existingSub = await registration.pushManager.getSubscription();

    if (existingSub) {
      await existingSub.unsubscribe();
      console.log('[Push] Subscription antiga removida');
      return true;
    }

    return false;
  }

  async getExistingPushSubscription() {
    const existingSub = await firstValueFrom(
      this.swPush.subscription.pipe(take(1))
    );

    if (existingSub) {
      return existingSub;
    }

    return null;
  }

  async verifyValidSubscription(userId: string, endpoint: string): Promise<boolean | null> {

    const { count, error } = await this.supabase
      .from(TYPES.DATABASE.TABLES.PUSH_SUBSCRIPTIONS)
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("endpoint", endpoint);

    if (error) {
      console.error("Erro ao contar logs:", error);
      throw error;
    }

    return (count ?? 0) > 0;

  }

  async removeValidSubscription(userId: string): Promise<void> {

    if (!this.swPush.isEnabled) {
      return;
    }

    const existSub = await this.getExistingPushSubscription();

    if(existSub){
      const { error } = await this.supabase
        .from(TYPES.DATABASE.TABLES.PUSH_SUBSCRIPTIONS)
        .delete()
        .eq("user_id", userId)
        .eq("endpoint", existSub.endpoint);

      if (error) {
        console.error("Erro ao contar logs:", error);
        throw error;
      }

      await this.cancelExistingPushSubscription();
    }

  }




}
